import {global} from "../appContext";
import {ICompanySyncstep, IExportFlow, IExportTemplate, IRuleset} from "../index";
import {MAX_RESULTS_PER_PAGE} from "../constants";
import {_} from 'lodash';
import {nsSyncstep} from "../component/sync-steps/fn";
import {nsClone} from "./fn";
import PromiseRequest from "../req/PromiseRequest";
import {nsRuleset} from "../component/ruleset/nsRuleset";
import pubsub from "../pubsub";
import {ICloneDialogOptions} from "./index";

export namespace nsSynchronizationTemplateClone {

    /**
     * Persists a synchronization template already prepared for the target org (sync step, ruleset links, name).
     * Used by the copy wizard after rulesets are copied and names are resolved.
     */
    export function persistPreparedCopy(
        src: IExportTemplate,
        trgOrgnr: string,
        rulesetSourceToTarget: Map<number, IRuleset>,
        params: {name: string} = {name: src.name},
    ): Promise<IExportTemplate> {
        let cpy: IExportTemplate = _.extend(_.cloneDeep(src), params);
        cpy.id = 0;
        cpy.syncstep.id = 0;
        cpy.exportflow = [];
        cpy.active = false;

        const srcFlows = src.exportflow || [];

        return Promise.resolve()
            .then(() =>
                nsSyncstep.find(src.syncstep.syncstep.id, trgOrgnr).then((data: ICompanySyncstep) => {
                    if (data) {
                        return [data];
                    }
                    return nsSyncstep
                        .create(cpy.syncstep, trgOrgnr)
                        .then(() => nsSyncstep.find(src.syncstep.syncstep.id, trgOrgnr))
                        .then((data: ICompanySyncstep) => [data]);
                }),
            )
            .then((data: ICompanySyncstep[]) => {
                cpy.syncstep = data[0]!;
            })
            .then(() => {
                if (cpy.ruleset) {
                    const mapped = rulesetSourceToTarget.get(cpy.ruleset.id);
                    if (!mapped) {
                        throw new Error("ruleset missing");
                    }
                    cpy.ruleset = mapped;
                }
            })
            .then(() =>
                new PromiseRequest("createexporttemplate")
                    .request({
                        jexporttemplate: JSON.stringify(cpy),
                        orgnr: trgOrgnr,
                    })
                    .then((data: IExportTemplate[]) => {
                        cpy = data[0]!;
                    }),
            )
            .then(() =>
                Promise.all(
                    srcFlows.map((flow: IExportFlow) => {
                        const cpyFlow = _.cloneDeep(flow);
                        cpyFlow.syncstep = cpy.syncstep;
                        if (cpyFlow.ruleset) {
                            const mapped = rulesetSourceToTarget.get(flow.ruleset.id);
                            if (!mapped) {
                                throw new Error("ruleset missing");
                            }
                            cpyFlow.ruleset = mapped;
                        }
                        return new PromiseRequest("createexportflow").request({
                            exporttemplateid: cpy.id,
                            jexportflow: JSON.stringify(cpyFlow),
                            orgnr: trgOrgnr,
                        });
                    }),
                ),
            )
            .then(() =>
                new PromiseRequest("getexporttemplate")
                    .request({
                        orgnr: trgOrgnr,
                        id: cpy.id,
                    })
                    .then((data: IExportTemplate[]) => {
                        const created = data[0];
                        if (!created) {
                            throw new Error(`Missing export template id=${cpy.id}`);
                        }
                        return created;
                    }),
            );
    }

    export function create(src: IExportTemplate, srcOrgnr: string, trgOrgnr: string, params: any = {}): Promise<any> {
        const isSrcTrgDifferent = srcOrgnr != trgOrgnr;
        let cpy: IExportTemplate = _.extend(_.cloneDeep(src), params);

        // id will be created
        cpy.id = 0;
        cpy.syncstep.id = 0;

        // export flows will be created - empty for now
        cpy.exportflow = [];
        cpy.active = false;

        const rulesetsInUse: IRuleset[] = [src.ruleset, ...src.exportflow.map(flow => flow.ruleset)].filter(Boolean);
        const rulesetsInUseUnique: { id: number, src: IRuleset, trg: IRuleset | null }[] = [];
        _.uniqBy(rulesetsInUse, (rs: IRuleset) => rs.id)
            .forEach((rs: IRuleset) => {
                rulesetsInUseUnique.push({
                    id: rs.id,
                    src: rs,
                    trg: isSrcTrgDifferent ? null : rs, // if srcOrg != trgOrg then populate trg below
                })
            })
        // Execute promises
        return Promise.resolve()
            // retrieve all unique rulesets in use
            .then(() => {
                if (isSrcTrgDifferent) {
                    // create rulesets and populate trg property
                    return Promise.all(rulesetsInUseUnique.map(rulesetInUse => nsRuleset
                        .create(rulesetInUse.src, trgOrgnr)
                        .then((data: IRuleset[]) => rulesetInUse.trg = data[0] ?? null))
                    )
                }
            })
            .then(() => {
                // --- COMPANY SYNCSTEP --------------------------------------------------------------------------------
                return nsSyncstep.find(src.syncstep.syncstep.id, trgOrgnr)
                    .then((data: ICompanySyncstep) => {
                        if (data) {
                            // return existing
                            return [data];
                        } else {
                            // create new
                            return nsSyncstep
                                .create(cpy.syncstep, trgOrgnr)
                                .then(() => nsSyncstep.find(src.syncstep.syncstep.id, trgOrgnr))
                                // FIXME:
                                // This is to overcome an API bug in which a newly created company syncstep
                                // doesn't contain a valid id. By fetching all syncsteps again we'll find the new id as
                                // well.
                                .then((data: ICompanySyncstep) => [data]);
                        }
                    })
                    .then((data: ICompanySyncstep[]) => cpy.syncstep = data[0]!);
            })
            .then(() => {
                // --- RULESET -----------------------------------------------------------------------------------------
                if (cpy.ruleset) {
                    cpy.ruleset = rulesetsInUseUnique.find(rs => rs.id == cpy.ruleset.id)!.trg!;
                }
            })
            .then(() => {
                // --- EXPORT TEMPLATE ---------------------------------------------------------------------------------
                return new PromiseRequest("createexporttemplate")
                    .request({
                        jexporttemplate: JSON.stringify(cpy),
                        orgnr: trgOrgnr
                    })
                    .then((data: IExportTemplate[]) => {
                        global.cacheManager.removeCache('getruleset');
                        global.cacheManager.removeCache('searchruleset');
                        global.cacheManager.removeCache("searchexporttemplate");
                        // replace the clone with the newly created export template
                        cpy = data[0]!;
                    });
            })
            .then(() => {
                // --- EXPORT FLOWS ------------------------------------------------------------------------------------
                return Promise.all(src.exportflow.map((flow: IExportFlow) => {
                    const cpyFlow = _.cloneDeep(flow);
                    cpyFlow.syncstep = cpy.syncstep;
                    Promise.resolve()
                        .then(() => {
                            if (cpyFlow.ruleset) {
                                cpyFlow.ruleset = rulesetsInUseUnique.find(rs => rs.id == flow.ruleset.id)!.trg!;
                            } else {
                                return Promise.resolve();
                            }
                        })
                        .then(() => new PromiseRequest("createexportflow")
                            .request({
                                exporttemplateid: cpy.id,
                                jexportflow: JSON.stringify(cpyFlow),
                                orgnr: trgOrgnr,
                            }))
                }));
            })
            .then(() => {
                global.cacheManager.removeCache('getexporttemplate');
                global.cacheManager.removeCache('searchexporttemplate');
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchruleset');
                return new PromiseRequest("getexporttemplate")
                    .request({
                        orgnr: trgOrgnr,
                        id: cpy.id,
                    });
            });
    }

    export function trash(exportTemplate: IExportTemplate): Promise<any> {
        return new PromiseRequest("deleteexporttemplate")
            .request({id: exportTemplate.id})
            .then(() => {
                global.cacheManager.removeCache("searchexporttemplate");
                global.cacheManager.removeCache("getexporttemplate");
                global.cacheManager.removeCache("searchruleset");
                global.cacheManager.removeCache("getruleset");
                pubsub.trigger('delete.synctemplate.complete');
            });
    }

    const DialogOptions: ICloneDialogOptions = {icon: 'fa-sync-template', translationKey: 'synchronization template'}

    export function copyDialog(exportTemplate: IExportTemplate) {
        nsClone.dialogCopy(DialogOptions, "component/export-template/copy-wizard/main", exportTemplate);
    }

    export function trashDialog(exportTemplate: IExportTemplate) {
        nsClone.dialogDelete(DialogOptions, "component/export-template/dialog/delete", exportTemplate);
    }

    export function getAll() {
        return new PromiseRequest("searchexporttemplate")
            .request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr: global.login.selectedOrganizationId(),
                sortcol: "name",
                asc: true
            });
    }

}
